import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Loader2, LogOut, ShieldCheck, MapPin, Plus, Trash2, Upload, CheckCircle2,
  AlertTriangle, Clock, Star, Send, Building2, CreditCard, Smartphone, Infinity as InfinityIcon,
  PoundSterling, TrendingUp, Trophy, Percent,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { marketplaceApi } from "@/services/api";
import { apiError, fmtMoney, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import ContractorLeads from "@/pages/partner/ContractorLeads";

const STATUS_META = {
  applied: { tone: "bg-slate-100 text-slate-700", title: "Draft application", msg: "Complete your profile below, then submit it for review." },
  under_review: { tone: "bg-amber-100 text-amber-800", title: "Under review", msg: "FD Check is reviewing your details. We'll be in touch — you can keep your profile up to date meanwhile." },
  approved: { tone: "bg-emerald-100 text-emerald-800", title: "Approved", msg: "You're live. You'll receive remedial leads for the postcode areas you cover." },
  suspended: { tone: "bg-red-100 text-red-700", title: "Suspended", msg: "Your account is paused. Please contact FD Check to reinstate it." },
  rejected: { tone: "bg-red-100 text-red-700", title: "Not approved", msg: "Your application wasn't approved. Update your details and submit again." },
};

const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function ContractorPortal() {
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("applied");
  const [alerts, setAlerts] = useState([]);
  const [f, setF] = useState(null);
  const [view, setView] = useState("profile");
  const didInit = React.useRef(false);

  const hydrate = (d) => {
    setStatus(d.status || "applied");
    setAlerts(d.alerts || []);
    setF({
      name: d.name || "", contact_name: d.contact_name || "", phone: d.phone || "",
      address: d.address || "", postcode: d.postcode || "", company_number: d.company_number || "",
      vat_number: d.vat_number || "", website: d.website || "",
      territoriesText: (d.territories || []).join(", "),
      insurances: d.insurances || [], qualifications: d.qualifications || [], accreditations: d.accreditations || [],
      social: d.social_links || {},
      google_rating: d.google_rating ?? "", google_reviews_url: d.google_reviews_url || "", google_reviews_count: d.google_reviews_count ?? "",
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await marketplaceApi.me();
        hydrate(data);
        if (!didInit.current) { didInit.current = true; setView(data.status === "approved" ? "leads" : "profile"); }
      }
      catch (e) { toast.error(apiError(e, "Could not load your profile")); }
      finally { setLoading(false); }
    })();
  }, []);

  const buildPayload = () => ({
    name: f.name, contact_name: f.contact_name, phone: f.phone, address: f.address, postcode: f.postcode,
    company_number: f.company_number, vat_number: f.vat_number, website: f.website,
    territories: f.territoriesText.split(",").map((t) => t.trim()).filter(Boolean),
    insurances: (f.insurances || []).map((r) => ({ ...r, cover_amount: numOrNull(r.cover_amount) })),
    qualifications: f.qualifications || [],
    accreditations: f.accreditations || [],
    social_links: f.social || {},
    google_rating: numOrNull(f.google_rating),
    google_reviews_url: f.google_reviews_url,
    google_reviews_count: numOrNull(f.google_reviews_count),
  });

  const save = async () => {
    setSaving(true);
    try { const { data } = await marketplaceApi.updateMe(buildPayload()); hydrate(data); toast.success("Profile saved"); return true; }
    catch (e) { toast.error(apiError(e, "Could not save")); return false; }
    finally { setSaving(false); }
  };

  const submit = async () => {
    const ok = await save();
    if (!ok) return;
    setSubmitting(true);
    try { const { data } = await marketplaceApi.submitMe(); hydrate(data); toast.success("Submitted for review"); }
    catch (e) { toast.error(apiError(e, "Please complete the required fields first")); }
    finally { setSubmitting(false); }
  };

  const upload = async (file, kind, apply) => {
    try {
      const dataUrl = await readFileAsBase64(file);
      const { data } = await marketplaceApi.uploadDocument({ filename: file.name, mime: file.type, kind, data_base64: dataUrl });
      apply(data);
      toast.success("Document uploaded");
    } catch (e) { toast.error(apiError(e, "Upload failed")); }
  };

  if (loading || !f) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-red-600" /></div>;
  }

  const meta = STATUS_META[status] || STATUS_META.applied;
  const setRow = (key, idx, patch) => setF((s) => ({ ...s, [key]: s[key].map((r, i) => (i === idx ? { ...r, ...patch } : r)) }));
  const addRow = (key, blank) => setF((s) => ({ ...s, [key]: [...(s[key] || []), blank] }));
  const delRow = (key, idx) => setF((s) => ({ ...s, [key]: s[key].filter((_, i) => i !== idx) }));

  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="partner-portal">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img src="/fdcheck-logo.jpg" alt="FD Check" className="h-8 w-auto object-contain" />
            <span className="text-sm font-semibold text-slate-700">Contractor portal</span>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="partner-logout"><LogOut className="mr-1.5 h-4 w-4" /> Sign out</Button>
        </div>
        <div className="mx-auto flex max-w-4xl gap-1 px-4">
          <TabBtn active={view === "leads"} onClick={() => setView("leads")} testId="partner-tab-leads">Available work</TabBtn>
          <TabBtn active={view === "performance"} onClick={() => setView("performance")} testId="partner-tab-performance">Performance</TabBtn>
          <TabBtn active={view === "plan"} onClick={() => setView("plan")} testId="partner-tab-plan">Plan &amp; territories</TabBtn>
          <TabBtn active={view === "profile"} onClick={() => setView("profile")} testId="partner-tab-profile">My profile</TabBtn>
        </div>
      </header>

      {view === "leads" && (
        <main className="mx-auto max-w-4xl px-4 py-6">
          <ContractorLeads approved={status === "approved"} />
        </main>
      )}

      {view === "plan" && (
        <main className="mx-auto max-w-4xl px-4 py-6">
          <ContractorPlan />
        </main>
      )}

      {view === "performance" && (
        <main className="mx-auto max-w-4xl px-4 py-6">
          <ContractorPerformance />
        </main>
      )}

      {view === "profile" && (
      <>
      <main className="mx-auto max-w-4xl space-y-4 px-4 py-6" data-testid="partner-profile">
        {/* Status */}
        <Card className="p-5" data-testid="partner-status">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <Badge className={`${meta.tone} border-0`}>{meta.title}</Badge>
              <p className="mt-2 text-sm text-slate-600">{meta.msg}</p>
            </div>
            <Button onClick={submit} disabled={submitting || saving} className="bg-red-600 hover:bg-red-700" data-testid="partner-submit">
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Submit for review
            </Button>
          </div>
        </Card>

        {/* Expiry alerts */}
        {alerts.length > 0 && (
          <Card className="border-amber-200 bg-amber-50 p-4" data-testid="partner-alerts">
            <p className="flex items-center gap-2 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" /> Documents needing attention</p>
            <ul className="mt-2 space-y-1">
              {alerts.map((a, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-amber-800">
                  <Clock className="h-3.5 w-3.5" />
                  <span className="font-medium">{a.label}</span>
                  {a.status === "expired" ? <span className="text-red-600">expired</span> : <span>expires in {a.days_left} day{a.days_left === 1 ? "" : "s"}</span>}
                  {a.expires_on ? <span className="text-amber-600">({a.expires_on})</span> : null}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Company */}
        <Section icon={Building2} title="Company details">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Company name *"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="partner-name" /></Field>
            <Field label="Contact name"><Input value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} /></Field>
            <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
            <Field label="Website"><Input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} /></Field>
            <Field label="Address"><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></Field>
            <Field label="Postcode"><Input value={f.postcode} onChange={(e) => setF({ ...f, postcode: e.target.value })} /></Field>
            <Field label="Company number"><Input value={f.company_number} onChange={(e) => setF({ ...f, company_number: e.target.value })} /></Field>
            <Field label="VAT number"><Input value={f.vat_number} onChange={(e) => setF({ ...f, vat_number: e.target.value })} /></Field>
          </div>
        </Section>

        {/* Coverage */}
        <Section icon={MapPin} title="Coverage areas">
          <Field label="Postcode districts you cover">
            <Input value={f.territoriesText} onChange={(e) => setF({ ...f, territoriesText: e.target.value })} placeholder="e.g. SW1, M1, EC1, LS" data-testid="partner-territories" />
          </Field>
          <p className="mt-1.5 text-xs text-slate-500">Comma-separated outward codes or areas. You&apos;ll receive leads whose building postcode falls in these.</p>
        </Section>

        {/* Insurance */}
        <Section icon={ShieldCheck} title="Insurance" onAdd={() => addRow("insurances", { type: "Public Liability", insurer: "", policy_number: "", cover_amount: "", expires_on: "", document_url: "", document_name: "" })} addTestId="partner-insurance-add">
          {(f.insurances || []).length === 0 && <Empty text="Add your public liability and employer's liability cover." />}
          {(f.insurances || []).map((r, i) => (
            <RowCard key={i} onDelete={() => delRow("insurances", i)} testId={`partner-insurance-${i}`}>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Type"><Input value={r.type} onChange={(e) => setRow("insurances", i, { type: e.target.value })} /></Field>
                <Field label="Insurer"><Input value={r.insurer} onChange={(e) => setRow("insurances", i, { insurer: e.target.value })} /></Field>
                <Field label="Cover amount (£)"><Input type="number" value={r.cover_amount ?? ""} onChange={(e) => setRow("insurances", i, { cover_amount: e.target.value })} /></Field>
                <Field label="Expires on"><Input type="date" value={(r.expires_on || "").slice(0, 10)} onChange={(e) => setRow("insurances", i, { expires_on: e.target.value })} data-testid={`partner-insurance-expiry-${i}`} /></Field>
              </div>
              <UploadRow kind="insurance" name={r.document_name} onFile={(file) => upload(file, "insurance", (doc) => setRow("insurances", i, { document_url: doc.url, document_name: doc.filename }))} testId={`partner-insurance-upload-${i}`} />
            </RowCard>
          ))}
        </Section>

        {/* Qualifications */}
        <Section icon={CheckCircle2} title="Training & qualifications" onAdd={() => addRow("qualifications", { name: "", body: "", reference: "", expires_on: "", document_url: "", document_name: "" })} addTestId="partner-qual-add">
          {(f.qualifications || []).length === 0 && <Empty text="Add fire-door inspection / maintenance qualifications (e.g. FDIS, BM TRADA)." />}
          {(f.qualifications || []).map((r, i) => (
            <RowCard key={i} onDelete={() => delRow("qualifications", i)} testId={`partner-qual-${i}`}>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Qualification"><Input value={r.name} onChange={(e) => setRow("qualifications", i, { name: e.target.value })} /></Field>
                <Field label="Awarding body"><Input value={r.body} onChange={(e) => setRow("qualifications", i, { body: e.target.value })} /></Field>
                <Field label="Reference"><Input value={r.reference} onChange={(e) => setRow("qualifications", i, { reference: e.target.value })} /></Field>
                <Field label="Expires on"><Input type="date" value={(r.expires_on || "").slice(0, 10)} onChange={(e) => setRow("qualifications", i, { expires_on: e.target.value })} /></Field>
              </div>
              <UploadRow kind="qualification" name={r.document_name} onFile={(file) => upload(file, "qualification", (doc) => setRow("qualifications", i, { document_url: doc.url, document_name: doc.filename }))} testId={`partner-qual-upload-${i}`} />
            </RowCard>
          ))}
        </Section>

        {/* Accreditations */}
        <Section icon={ShieldCheck} title="Accreditations & registered bodies" onAdd={() => addRow("accreditations", { scheme: "", reference: "", expires_on: "" })} addTestId="partner-accred-add">
          {(f.accreditations || []).length === 0 && <Empty text="Add third-party schemes (e.g. BWF-CERTIFIRE, FIRAS, BM TRADA Q-Mark)." />}
          {(f.accreditations || []).map((r, i) => (
            <RowCard key={i} onDelete={() => delRow("accreditations", i)} testId={`partner-accred-${i}`}>
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Scheme"><Input value={r.scheme} onChange={(e) => setRow("accreditations", i, { scheme: e.target.value })} /></Field>
                <Field label="Reference"><Input value={r.reference} onChange={(e) => setRow("accreditations", i, { reference: e.target.value })} /></Field>
                <Field label="Expires on"><Input type="date" value={(r.expires_on || "").slice(0, 10)} onChange={(e) => setRow("accreditations", i, { expires_on: e.target.value })} /></Field>
              </div>
            </RowCard>
          ))}
        </Section>

        {/* Ratings & social */}
        <Section icon={Star} title="Ratings & social">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Google rating"><Input type="number" step="0.1" min="0" max="5" value={f.google_rating ?? ""} onChange={(e) => setF({ ...f, google_rating: e.target.value })} /></Field>
            <Field label="Google reviews count"><Input type="number" value={f.google_reviews_count ?? ""} onChange={(e) => setF({ ...f, google_reviews_count: e.target.value })} /></Field>
            <Field label="Google reviews URL"><Input value={f.google_reviews_url} onChange={(e) => setF({ ...f, google_reviews_url: e.target.value })} /></Field>
            <Field label="Facebook"><Input value={f.social.facebook || ""} onChange={(e) => setF({ ...f, social: { ...f.social, facebook: e.target.value } })} /></Field>
            <Field label="Instagram"><Input value={f.social.instagram || ""} onChange={(e) => setF({ ...f, social: { ...f.social, instagram: e.target.value } })} /></Field>
            <Field label="LinkedIn"><Input value={f.social.linkedin || ""} onChange={(e) => setF({ ...f, social: { ...f.social, linkedin: e.target.value } })} /></Field>
          </div>
        </Section>
      </main>

      {/* Sticky save bar */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-end gap-3 px-4 py-3">
          <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="partner-save">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save profile
          </Button>
        </div>
      </div>
      </>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${active ? "border-red-600 text-red-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}
    >
      {children}
    </button>
  );
}

function Section({ icon: Icon, title, children, onAdd, addTestId }) {
  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-semibold text-slate-800"><Icon className="h-4 w-4 text-red-600" /> {title}</h3>
        {onAdd && <Button variant="outline" size="sm" onClick={onAdd} data-testid={addTestId}><Plus className="mr-1.5 h-4 w-4" /> Add</Button>}
      </div>
      <div className="space-y-3">{children}</div>
    </Card>
  );
}

function RowCard({ children, onDelete, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" data-testid={testId}>
      <div className="flex justify-end">
        <button type="button" onClick={onDelete} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
      </div>
      {children}
    </div>
  );
}

function UploadRow({ name, onFile, testId }) {
  return (
    <div className="mt-3 flex items-center gap-3">
      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50" data-testid={testId}>
        <Upload className="h-3.5 w-3.5" /> {name ? "Replace file" : "Upload certificate"}
        <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => { const file = e.target.files?.[0]; if (file) onFile(file); e.target.value = ""; }} />
      </label>
      {name && <span className="flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> {name}</span>}
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-sm text-slate-400">{text}</p>;
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs text-slate-600">{label}</Label>{children}</div>;
}

function ContractorPlan() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { const { data } = await marketplaceApi.mySubscription(); setData(data); }
    catch (e) { toast.error(apiError(e, "Could not load your plan")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-red-600" /></div>;
  }

  const sub = data.subscription || {};
  const allowance = data.allowance; // null = unlimited, 0 = none, N = areas
  const used = data.territories_used || 0;
  const unlimited = allowance === null && sub.active;
  const active = !!sub.active;
  const products = data.catalogue?.products || [];
  const fmtAllow = unlimited ? "Unlimited" : allowance || 0;

  return (
    <div className="space-y-4" data-testid="partner-plan">
      {/* Current subscription */}
      <Card className={`p-5 ${active ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}`} data-testid="partner-plan-status">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <CreditCard className={`h-5 w-5 ${active ? "text-emerald-600" : "text-amber-600"}`} />
              <h3 className="font-semibold text-slate-800">
                {active ? (sub.source === "complimentary" ? "Complimentary territory plan" : "Active territory subscription") : "No active territory plan"}
              </h3>
            </div>
            <p className="mt-1.5 text-sm text-slate-600">
              {active
                ? <>You can hold <span className="font-semibold">{unlimited ? "unlimited" : fmtAllow}</span> postcode area{fmtAllow === 1 ? "" : "s"} and receive remedial leads within them.</>
                : "Subscribe to a territory plan to receive remedial leads and quote for work in your area."}
            </p>
          </div>
          <Badge className={`border-0 ${active ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
            {active ? (sub.source === "complimentary" ? "Complimentary" : "Subscribed") : "Not subscribed"}
          </Badge>
        </div>

        {/* Usage */}
        <div className="mt-4 flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-sm text-slate-700">
            <MapPin className="h-4 w-4 text-slate-400" />
            <span className="font-semibold">{used}</span>
            <span>of {unlimited ? <InfinityIcon className="inline h-4 w-4 -mt-0.5" /> : fmtAllow} area{fmtAllow === 1 ? "" : "s"} used</span>
          </div>
          {!unlimited && allowance != null && used > allowance && (
            <span className="text-xs font-medium text-amber-700" data-testid="partner-plan-overcap">Over your plan — remove {used - allowance} or upgrade</span>
          )}
        </div>
        {sub.price_string && <p className="mt-2 text-xs text-slate-500">{sub.price_string}/year · billed by {sub.store === "play_store" ? "Google Play" : sub.store === "app_store" ? "Apple" : "the store"}{sub.will_renew === false ? " · auto-renew off" : ""}</p>}
      </Card>

      {/* Plans (read-only on web) */}
      <Card className="p-5">
        <h3 className="font-semibold text-slate-800">Territory plans</h3>
        <p className="mt-1 text-sm text-slate-500">Yearly plans — the more areas you cover, the more leads you receive.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {products.map((p) => {
            const isCurrent = active && sub.package_identifier === p.package;
            return (
              <div key={p.package} className={`rounded-xl border p-4 ${isCurrent ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white"}`} data-testid={`partner-plan-${p.package}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800">{p.label}</span>
                  {isCurrent && <Badge className="border-0 bg-emerald-100 text-emerald-700">Current</Badge>}
                </div>
                <p className="mt-1 text-lg font-bold text-slate-900">{p.price}<span className="text-sm font-medium text-slate-500"> / year</span></p>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Purchase happens in the app (IAP) */}
      <Card className="flex items-start gap-3 border-blue-200 bg-blue-50 p-4" data-testid="partner-plan-app-note">
        <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
        <div className="text-sm text-blue-900">
          <p className="font-semibold">Manage your subscription in the FD Check app</p>
          <p className="mt-1 text-blue-800">Territory subscriptions are purchased and cancelled through the FD Check mobile app (App Store / Google Play). Download the app and sign in with this account to subscribe or change your plan.</p>
        </div>
      </Card>
    </div>
  );
}

function ContractorPerformance() {
  const [loading, setLoading] = useState(true);
  const [d, setD] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await marketplaceApi.myPerformance(); setD(data); }
      catch (e) { toast.error(apiError(e, "Could not load your performance")); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !d) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-red-600" /></div>;

  const stats = [
    { icon: PoundSterling, label: "Won work value", value: fmtMoney(d.total_won_value), sub: "ex VAT, awarded to you", tone: "text-emerald-600" },
    { icon: TrendingUp, label: "Net after commission", value: fmtMoney(d.net_after_commission), sub: `${fmtMoney(d.commission_total)} platform fees`, tone: "text-slate-900" },
    { icon: Trophy, label: "Jobs won", value: d.won, sub: `${d.active_quotes} live quote${d.active_quotes === 1 ? "" : "s"}`, tone: "text-slate-900" },
    { icon: Percent, label: "Win rate", value: d.win_rate == null ? "—" : `${d.win_rate}%`, sub: `${d.quotes_sent} quote${d.quotes_sent === 1 ? "" : "s"} sent`, tone: "text-slate-900" },
  ];

  return (
    <div className="space-y-4" data-testid="partner-performance">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label} className="p-4" data-testid={`perf-stat-${s.label.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
            <s.icon className={`h-5 w-5 ${s.tone}`} />
            <p className={`mt-2 text-2xl font-bold ${s.tone}`}>{s.value}</p>
            <p className="text-xs font-medium text-slate-600">{s.label}</p>
            <p className="mt-0.5 text-xs text-slate-400">{s.sub}</p>
          </Card>
        ))}
      </div>

      <Card className="p-5" data-testid="perf-commission">
        <h3 className="flex items-center gap-2 font-semibold text-slate-800"><PoundSterling className="h-4 w-4 text-red-600" /> Platform commission</h3>
        <div className="mt-3 grid grid-cols-3 gap-3 text-center">
          <div><p className="text-lg font-bold text-slate-900">{fmtMoney(d.commission_total)}</p><p className="text-xs text-slate-500">Total</p></div>
          <div><p className="text-lg font-bold text-emerald-600">{fmtMoney(d.commission_paid)}</p><p className="text-xs text-slate-500">Paid</p></div>
          <div><p className="text-lg font-bold text-amber-600">{fmtMoney(d.commission_due)}</p><p className="text-xs text-slate-500">Outstanding</p></div>
        </div>
        <p className="mt-3 text-xs text-slate-400">Average quote value {fmtMoney(d.avg_bid)}.</p>
      </Card>

      <Card className="p-5">
        <h3 className="flex items-center gap-2 font-semibold text-slate-800"><Trophy className="h-4 w-4 text-red-600" /> Jobs you&apos;ve won</h3>
        {(!d.won_jobs || d.won_jobs.length === 0) ? (
          <p className="mt-3 text-sm text-slate-400">No awarded jobs yet. Quotes you win will appear here with the platform fee.</p>
        ) : (
          <div className="mt-3 space-y-2" data-testid="perf-won-list">
            {d.won_jobs.map((j) => (
              <div key={j.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 p-3" data-testid={`perf-won-${j.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-800">{j.title}</p>
                  <p className="truncate text-xs text-slate-500">{j.building_name || "Building"}{j.building_postcode ? ` · ${j.building_postcode}` : ""}{j.awarded_at ? ` · ${fmtDate(j.awarded_at)}` : ""}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-slate-900">{fmtMoney(j.amount)}</p>
                  {j.commission_fee != null && (
                    <p className={`text-xs font-medium ${j.commission_status === "paid" ? "text-emerald-600" : "text-amber-600"}`}>fee {fmtMoney(j.commission_fee)} · {j.commission_status || "due"}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

